import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface Badge {
  id: string
  name: string
  description: string
  icon: string
  color: string
  category: 'study' | 'exam' | 'streak' | 'achievement' | 'social'
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  unlockedAt?: string
  progress?: number
  maxProgress?: number
}

interface Achievement {
  id: string
  title: string
  description: string
  type: 'study_time' | 'exam_score' | 'streak' | 'mindmap' | 'social'
  target: number
  current: number
  completed: boolean
  completedAt?: string
  reward: {
    xp: number
    coins: number
    badge?: string
  }
}

interface Quest {
  id: string
  title: string
  description: string
  type: 'daily' | 'weekly' | 'monthly' | 'special'
  difficulty: 'easy' | 'medium' | 'hard'
  tasks: {
    id: string
    description: string
    type: 'study' | 'exam' | 'mindmap'
    target: number
    current: number
    completed: boolean
  }[]
  reward: {
    xp: number
    coins: number
    items?: string[]
  }
  deadline: string
  completed: boolean
  completedAt?: string
  isActive: boolean
}

export interface ShopItem {
  id: string
  name: string
  description: string
  type: 'theme' | 'avatar' | 'sticker' | 'background' | 'effect' | 'electronics' | 'book' | 'stationery' | 'gift_card' | 'accessory'
  price: number
  category: string
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  preview?: string
  owned: boolean
  equipped: boolean
  image?: string
  originalPrice?: number
  discount?: number
  isNew?: boolean
  isPopular?: boolean
  isPhysical?: boolean
  deliveryType?: 'instant' | 'shipping'
  estimatedDelivery?: string
}

interface CartItem {
  id: string
  quantity: number
  addedAt: string
}

interface Order {
  id: string
  items: {
    itemId: string
    quantity: number
    price: number
    name: string
    image?: string
    deliveryType: 'instant' | 'shipping'
  }[]
  totalAmount: number
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  orderDate: string
  deliveryDate?: string
  shippingAddress?: {
    fullName: string
    address: string
    city: string
    postalCode: string
    phone: string
  }
  trackingNumber?: string
  deliveryCode?: string
}

interface LeaderboardEntry {
  userId: string
  username: string
  avatar?: string
  level: number
  xp: number
  weeklyXP: number
  monthlyXP: number
  rank: number
  badges: string[]
}

interface GamificationState {
  level: number
  xp: number
  coins: number
  weeklyXP: number
  monthlyXP: number
  totalXP: number
  badges: Badge[]
  achievements: Achievement[]
  quests: Quest[]
  shopItems: ShopItem[]
  inventory: string[]
  equippedItems: {
    theme: string | null
    avatar: string | null
    background: string | null
  }
  cart: CartItem[]
  orders: Order[]
  leaderboard: LeaderboardEntry[]
  friends: string[]
  studyGroups: string[]
  stats: {
    totalStudyTime: number
    totalExams: number
    averageScore: number
    streakDays: number
    badgesEarned: number
    questsCompleted: number
    rank: number
    weeklyRank: number
  }
  notifications: {
    id: string
    type: 'badge' | 'achievement' | 'quest' | 'level' | 'friend'
    title: string
    message: string
    timestamp: string
    read: boolean
  }[]
  settings: {
    showNotifications: boolean
    showLeaderboard: boolean
    allowFriendRequests: boolean
    shareProgress: boolean
  }
}

const sampleBadges: Badge[] = [
  {
    id: 'first_study',
    name: 'İlk Adım',
    description: 'İlk çalışma oturumunu tamamla',
    icon: '🎯',
    color: '#10b981',
    category: 'study',
    rarity: 'common',
    unlockedAt: '2024-01-10T10:00:00Z'
  },
  {
    id: 'exam_master',
    name: 'Sınav Ustası',
    description: '10 deneme sınavını başarıyla tamamla',
    icon: '🏆',
    color: '#f59e0b',
    category: 'exam',
    rarity: 'rare',
    progress: 7,
    maxProgress: 10
  },
  {
    id: 'streak_warrior',
    name: 'Süreklilik Savaşçısı',
    description: '7 gün üst üste çalış',
    icon: '🔥',
    color: '#ef4444',
    category: 'streak',
    rarity: 'epic',
    unlockedAt: '2024-01-12T18:00:00Z'
  },

]

const sampleAchievements: Achievement[] = [
  {
    id: 'study_10h',
    title: '10 Saat Çalışma',
    description: 'Toplam 10 saat çalışma süresine ulaş',
    type: 'study_time',
    target: 36000, // 10 hours in seconds
    current: 28800, // 8 hours
    completed: false,
    reward: { xp: 500, coins: 100, badge: 'study_master' }
  },
  {
    id: 'perfect_score',
    title: 'Mükemmel Skor',
    description: 'Bir denemede %100 başarı elde et',
    type: 'exam_score',
    target: 100,
    current: 85,
    completed: false,
    reward: { xp: 1000, coins: 200, badge: 'perfectionist' }
  },
  {
    id: 'week_streak',
    title: 'Haftalık Süreklilik',
    description: '7 gün üst üste çalış',
    type: 'streak',
    target: 7,
    current: 5,
    completed: false,
    reward: { xp: 300, coins: 50 }
  }
]

const sampleQuests: Quest[] = [
  {
    id: 'daily_study',
    title: 'Günlük Çalışma',
    description: 'Bugün en az 1 saat çalış',
    type: 'daily',
    difficulty: 'easy',
    tasks: [
      {
        id: 'study_60min',
        description: '60 dakika çalış',
        type: 'study',
        target: 3600,
        current: 2400,
        completed: false
      }
    ],
    reward: { xp: 100, coins: 20 },
    deadline: '2024-01-16T23:59:59Z',
    completed: false,
    isActive: true
  },
  {
    id: 'weekly_exams',
    title: 'Haftalık Denemeler',
    description: 'Bu hafta 3 deneme sınavı çöz',
    type: 'weekly',
    difficulty: 'medium',
    tasks: [
      {
        id: 'solve_3_exams',
        description: '3 deneme sınavı çöz',
        type: 'exam',
        target: 3,
        current: 1,
        completed: false
      }
    ],
    reward: { xp: 300, coins: 60, items: ['rare_theme'] },
    deadline: '2024-01-21T23:59:59Z',
    completed: false,
    isActive: true
  }
]

const sampleShopItems: ShopItem[] = [
  {
    id: 'dark_theme',
    name: 'Karanlık Tema',
    description: 'Gözlerinizi koruyun, karanlık temayı kullanın',
    type: 'theme',
    price: 100,
    category: 'Temalar',
    rarity: 'common',
    owned: false,
    equipped: false
  },
  {
    id: 'galaxy_bg',
    name: 'Galaksi Arkaplanı',
    description: 'Uzayın derinliklerinde çalışın',
    type: 'background',
    price: 250,
    category: 'Arkaplanlar',
    rarity: 'rare',
    owned: false,
    equipped: false
  },
  {
    id: 'robot_avatar',
    name: 'Robot Avatar',
    description: 'Gelecekten gelen çalışma arkadaşınız',
    type: 'avatar',
    price: 150,
    category: 'Avatarlar',
    rarity: 'common',
    owned: true,
    equipped: false
  },
  {
    id: 'success_sticker',
    name: 'Başarı Sticker Paketi',
    description: 'Başarılarınızı kutlayın',
    type: 'sticker',
    price: 50,
    category: 'Stickerlar',
    rarity: 'common',
    owned: false,
    equipped: false
  }
]

const sampleLeaderboard: LeaderboardEntry[] = [
  {
    userId: '1',
    username: 'Ahmet Yılmaz',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    level: 12,
    xp: 2450,
    weeklyXP: 450,
    monthlyXP: 1200,
    rank: 1,
    badges: ['first_study', 'streak_warrior']
  },
  {
    userId: '2',
    username: 'Zeynep Kaya',
    level: 11,
    xp: 2200,
    weeklyXP: 380,
    monthlyXP: 1100,
    rank: 2,
    badges: ['exam_master']
  },
  {
    userId: '3',
    username: 'Mehmet Demir',
    level: 10,
    xp: 1950,
    weeklyXP: 320,
    monthlyXP: 950,
    rank: 3,
    badges: ['first_study']
  }
]

const initialState: GamificationState = {
  level: 12,
  xp: 2450,
  coins: 350,
  weeklyXP: 450,
  monthlyXP: 1200,
  totalXP: 5600,
  badges: sampleBadges,
  achievements: sampleAchievements,
  quests: sampleQuests,
  shopItems: sampleShopItems,
  inventory: ['robot_avatar'],
  equippedItems: {
    theme: null,
    avatar: null,
    background: null
  },
  cart: [],
  orders: [],
  leaderboard: sampleLeaderboard,
  friends: ['2', '3'],
  studyGroups: ['group1'],
  stats: {
    totalStudyTime: 28800, // 8 hours
    totalExams: 15,
    averageScore: 82,
    streakDays: 5,
    badgesEarned: 2,
    questsCompleted: 8,
    rank: 1,
    weeklyRank: 1
  },
  notifications: [
    {
      id: '1',
      type: 'badge',
      title: 'Yeni Rozet!',
      message: 'Süreklilik Savaşçısı rozetini kazandınız!',
      timestamp: '2024-01-15T10:30:00Z',
      read: false
    },
    {
      id: '2',
      type: 'achievement',
      title: 'Başarım Tamamlandı!',
      message: 'Haftalık Süreklilik başarımını tamamladınız!',
      timestamp: '2024-01-14T18:00:00Z',
      read: false
    }
  ],
  settings: {
    showNotifications: true,
    showLeaderboard: true,
    allowFriendRequests: true,
    shareProgress: true
  }
}

const gamificationSlice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    addXP: (state, action: PayloadAction<{ amount: number; source: string }>) => {
      const oldLevel = state.level
      state.xp += action.payload.amount
      state.weeklyXP += action.payload.amount
      state.monthlyXP += action.payload.amount
      state.totalXP += action.payload.amount
      
      // Calculate new level (200 XP per level)
      const newLevel = Math.floor(state.xp / 200) + 1
      
      if (newLevel > oldLevel) {
        state.level = newLevel
        state.coins += newLevel * 10 // Bonus coins for leveling up
        
        // Add level up notification
        state.notifications.unshift({
          id: Date.now().toString(),
          type: 'level',
          title: 'Seviye Atladınız!',
          message: `Tebrikler! ${newLevel}. seviyeye ulaştınız!`,
          timestamp: new Date().toISOString(),
          read: false
        })
      }
    },

    addCoins: (state, action: PayloadAction<number>) => {
      state.coins += action.payload
    },

    spendCoins: (state, action: PayloadAction<number>) => {
      state.coins = Math.max(0, state.coins - action.payload)
    },

    unlockBadge: (state, action: PayloadAction<string>) => {
      const badge = state.badges.find(b => b.id === action.payload)
      if (badge && !badge.unlockedAt) {
        badge.unlockedAt = new Date().toISOString()
        state.stats.badgesEarned += 1
        
        // Add notification
        state.notifications.unshift({
          id: Date.now().toString(),
          type: 'badge',
          title: 'Yeni Rozet!',
          message: `${badge.name} rozetini kazandınız!`,
          timestamp: new Date().toISOString(),
          read: false
        })
      }
    },

    updateBadgeProgress: (state, action: PayloadAction<{ badgeId: string; progress: number }>) => {
      const badge = state.badges.find(b => b.id === action.payload.badgeId)
      if (badge && badge.maxProgress) {
        badge.progress = Math.min(action.payload.progress, badge.maxProgress)
        
        if (badge.progress >= badge.maxProgress && !badge.unlockedAt) {
          gamificationSlice.caseReducers.unlockBadge(state, { payload: badge.id, type: 'gamification/unlockBadge' })
        }
      }
    },

    completeAchievement: (state, action: PayloadAction<string>) => {
      const achievement = state.achievements.find(a => a.id === action.payload)
      if (achievement && !achievement.completed) {
        achievement.completed = true
        achievement.completedAt = new Date().toISOString()
        
        // Add rewards
        state.xp += achievement.reward.xp
        state.coins += achievement.reward.coins
        state.totalXP += achievement.reward.xp
        
        if (achievement.reward.badge) {
          gamificationSlice.caseReducers.unlockBadge(state, { payload: achievement.reward.badge, type: 'gamification/unlockBadge' })
        }
        
        // Add notification
        state.notifications.unshift({
          id: Date.now().toString(),
          type: 'achievement',
          title: 'Başarım Tamamlandı!',
          message: `${achievement.title} başarımını tamamladınız!`,
          timestamp: new Date().toISOString(),
          read: false
        })
      }
    },

    updateAchievementProgress: (state, action: PayloadAction<{ achievementId: string; progress: number }>) => {
      const achievement = state.achievements.find(a => a.id === action.payload.achievementId)
      if (achievement && !achievement.completed) {
        achievement.current = Math.min(action.payload.progress, achievement.target)
        
        if (achievement.current >= achievement.target) {
          gamificationSlice.caseReducers.completeAchievement(state, { payload: achievement.id, type: 'gamification/completeAchievement' })
        }
      }
    },

    completeQuest: (state, action: PayloadAction<string>) => {
      const quest = state.quests.find(q => q.id === action.payload)
      if (quest && !quest.completed) {
        quest.completed = true
        quest.completedAt = new Date().toISOString()
        quest.isActive = false
        
        // Add rewards
        state.xp += quest.reward.xp
        state.coins += quest.reward.coins
        state.totalXP += quest.reward.xp
        state.stats.questsCompleted += 1
        
        if (quest.reward.items) {
          quest.reward.items.forEach(item => {
            if (!state.inventory.includes(item)) {
              state.inventory.push(item)
            }
          })
        }
        
        // Add notification
        state.notifications.unshift({
          id: Date.now().toString(),
          type: 'quest',
          title: 'Görev Tamamlandı!',
          message: `${quest.title} görevini tamamladınız!`,
          timestamp: new Date().toISOString(),
          read: false
        })
      }
    },

    updateQuestProgress: (state, action: PayloadAction<{ questId: string; taskId: string; progress: number }>) => {
      const quest = state.quests.find(q => q.id === action.payload.questId)
      if (quest && !quest.completed) {
        const task = quest.tasks.find(t => t.id === action.payload.taskId)
        if (task && !task.completed) {
          task.current = Math.min(action.payload.progress, task.target)
          
          if (task.current >= task.target) {
            task.completed = true
          }
          
          // Check if all tasks are completed
          const allTasksCompleted = quest.tasks.every(t => t.completed)
          if (allTasksCompleted) {
            gamificationSlice.caseReducers.completeQuest(state, { payload: quest.id, type: 'gamification/completeQuest' })
          }
        }
      }
    },

    purchaseItem: (state, action: PayloadAction<string>) => {
      const item = state.shopItems.find(i => i.id === action.payload)
      if (item && !item.owned && state.coins >= item.price) {
        item.owned = true
        state.coins -= item.price
        
        if (!state.inventory.includes(item.id)) {
          state.inventory.push(item.id)
        }
      }
    },

    equipItem: (state, action: PayloadAction<{ itemId: string; type: 'theme' | 'avatar' | 'background' }>) => {
      const item = state.shopItems.find(i => i.id === action.payload.itemId)
      if (item && item.owned) {
        // Unequip current item of same type
        state.shopItems.forEach(i => {
          if (i.type === action.payload.type) {
            i.equipped = false
          }
        })
        
        // Equip new item
        item.equipped = true
        state.equippedItems[action.payload.type] = action.payload.itemId
      }
    },

    unequipItem: (state, action: PayloadAction<'theme' | 'avatar' | 'background'>) => {
      const currentItem = state.shopItems.find(i => i.id === state.equippedItems[action.payload])
      if (currentItem) {
        currentItem.equipped = false
      }
      state.equippedItems[action.payload] = null
    },

    addFriend: (state, action: PayloadAction<string>) => {
      if (!state.friends.includes(action.payload)) {
        state.friends.push(action.payload)
      }
    },

    removeFriend: (state, action: PayloadAction<string>) => {
      state.friends = state.friends.filter(id => id !== action.payload)
    },

    joinStudyGroup: (state, action: PayloadAction<string>) => {
      if (!state.studyGroups.includes(action.payload)) {
        state.studyGroups.push(action.payload)
      }
    },

    leaveStudyGroup: (state, action: PayloadAction<string>) => {
      state.studyGroups = state.studyGroups.filter(id => id !== action.payload)
    },

    updateStats: (state, action: PayloadAction<Partial<typeof initialState.stats>>) => {
      state.stats = { ...state.stats, ...action.payload }
    },

    markNotificationRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },

    markAllNotificationsRead: (state) => {
      state.notifications.forEach(n => n.read = true)
    },

    deleteNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },

    clearNotifications: (state) => {
      state.notifications = []
    },

    updateSettings: (state, action: PayloadAction<Partial<typeof initialState.settings>>) => {
      state.settings = { ...state.settings, ...action.payload }
    },

    updateLeaderboard: (state, action: PayloadAction<LeaderboardEntry[]>) => {
      state.leaderboard = action.payload
    },

    resetWeeklyXP: (state) => {
      state.weeklyXP = 0
    },

    resetMonthlyXP: (state) => {
      state.monthlyXP = 0
    },

    addQuest: (state, action: PayloadAction<Omit<Quest, 'id'>>) => {
      const newQuest: Quest = {
        ...action.payload,
        id: Date.now().toString()
      }
      state.quests.push(newQuest)
    },

    removeQuest: (state, action: PayloadAction<string>) => {
      state.quests = state.quests.filter(q => q.id !== action.payload)
    },

    addShopItem: (state, action: PayloadAction<Omit<ShopItem, 'owned' | 'equipped'>>) => {
      const newItem: ShopItem = {
        ...action.payload,
        owned: false,
        equipped: false
      }
      state.shopItems.push(newItem)
    },

    removeShopItem: (state, action: PayloadAction<string>) => {
      state.shopItems = state.shopItems.filter(i => i.id !== action.payload)
      state.inventory = state.inventory.filter(id => id !== action.payload)
    },

    // Cart Management
    addToCart: (state, action: PayloadAction<{ itemId: string; quantity?: number }>) => {
      const { itemId, quantity = 1 } = action.payload
      const existingItem = state.cart.find(item => item.id === itemId)
      
      if (existingItem) {
        existingItem.quantity += quantity
      } else {
        state.cart.push({
          id: itemId,
          quantity,
          addedAt: new Date().toISOString()
        })
      }
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cart = state.cart.filter(item => item.id !== action.payload)
    },

    updateCartQuantity: (state, action: PayloadAction<{ itemId: string; quantity: number }>) => {
      const { itemId, quantity } = action.payload
      const item = state.cart.find(item => item.id === itemId)
      
      if (item) {
        if (quantity <= 0) {
          state.cart = state.cart.filter(item => item.id !== itemId)
        } else {
          item.quantity = quantity
        }
      }
    },

    clearCart: (state) => {
      state.cart = []
    },

    // Order Management
    createOrder: (state, action: PayloadAction<Omit<Order, 'id' | 'orderDate' | 'status'>>) => {
      const newOrder: Order = {
        ...action.payload,
        id: `order_${Date.now()}`,
        orderDate: new Date().toISOString(),
        status: 'pending'
      }
      state.orders.unshift(newOrder)
      state.cart = [] // Clear cart after order
    },

    updateOrderStatus: (state, action: PayloadAction<{ orderId: string; status: Order['status']; trackingNumber?: string; deliveryCode?: string }>) => {
      const { orderId, status, trackingNumber, deliveryCode } = action.payload
      const order = state.orders.find(order => order.id === orderId)
      
      if (order) {
        order.status = status
        if (trackingNumber) order.trackingNumber = trackingNumber
        if (deliveryCode) order.deliveryCode = deliveryCode
        if (status === 'delivered') {
          order.deliveryDate = new Date().toISOString()
        }
      }
    },

    cancelOrder: (state, action: PayloadAction<string>) => {
      const order = state.orders.find(order => order.id === action.payload)
      if (order && order.status === 'pending') {
        order.status = 'cancelled'
        // Refund coins
        state.coins += order.totalAmount
      }
    }
  },
})

export const {
  addXP,
  addCoins,
  spendCoins,
  unlockBadge,
  updateBadgeProgress,
  completeAchievement,
  updateAchievementProgress,
  completeQuest,
  updateQuestProgress,
  purchaseItem,
  equipItem,
  unequipItem,
  addFriend,
  removeFriend,
  joinStudyGroup,
  leaveStudyGroup,
  updateStats,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  clearNotifications,
  updateSettings,
  updateLeaderboard,
  resetWeeklyXP,
  resetMonthlyXP,
  addQuest,
  removeQuest,
  addShopItem,
  removeShopItem,
  addToCart,
  removeFromCart,
  updateCartQuantity,
  clearCart,
  createOrder,
  updateOrderStatus,
  cancelOrder,
} = gamificationSlice.actions

export default gamificationSlice.reducer